const DEFAULT_CLARITY_PROJECT_ID = 'wqdorcl72q'
const DEFAULT_CLARITY_APP_NAME = 'ogroshor-landing-platform'
const CLARITY_SCRIPT_ID = 'socialscale-clarity-script'
const TRACKING_QUERY_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'] as const

type ClarityFn = ((...args: unknown[]) => void) & {
  q?: unknown[][]
}

declare global {
  interface Window {
    clarity?: ClarityFn
  }
}

let clarityInitialized = false

function getProjectId(): string {
  const configuredProjectId = import.meta.env.VITE_MICROSOFT_CLARITY_PROJECT_ID?.trim()
  return configuredProjectId && configuredProjectId.length > 0
    ? configuredProjectId
    : DEFAULT_CLARITY_PROJECT_ID
}

function getAppName(): string {
  const configuredAppName = import.meta.env.VITE_MICROSOFT_CLARITY_APP_NAME?.trim()
  return configuredAppName && configuredAppName.length > 0
    ? configuredAppName
    : DEFAULT_CLARITY_APP_NAME
}

function isClarityEnabled(): boolean {
  if (import.meta.env.VITE_MICROSOFT_CLARITY_ENABLED === 'false') return false
  if (typeof navigator !== 'undefined' && navigator.doNotTrack === '1') return false
  return true
}

function normalizeClarityValue(value: string): string | null {
  const normalized = value.trim()
  if (!normalized) return null
  return normalized.slice(0, 128)
}

function callClarity(...args: unknown[]): void {
  if (typeof window === 'undefined') return
  if (typeof window.clarity !== 'function') return
  window.clarity(...args)
}

export function setClarityTags(tags: Record<string, string | number | boolean | null | undefined>): void {
  for (const [key, rawValue] of Object.entries(tags)) {
    if (rawValue === undefined || rawValue === null) continue
    const value = normalizeClarityValue(String(rawValue))
    if (!value) continue
    callClarity('set', key, value)
  }
}

export function initializeClarity(): void {
  if (!isClarityEnabled()) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (clarityInitialized) return

  const projectId = getProjectId()
  if (!projectId) return

  if (document.getElementById(CLARITY_SCRIPT_ID)) {
    clarityInitialized = true
    return
  }

  const clarity = ((...args: unknown[]) => {
    clarity.q = clarity.q ?? []
    clarity.q.push(args)
  }) as ClarityFn

  window.clarity = window.clarity ?? clarity

  const script = document.createElement('script')
  script.async = true
  script.id = CLARITY_SCRIPT_ID
  script.src = `https://www.clarity.ms/tag/${projectId}`

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  setClarityTags({
    app_name: getAppName(),
    app_env: import.meta.env.MODE,
  })

  clarityInitialized = true
}

export function setClarityRoute(path: string): void {
  const route = normalizeClarityValue(path)
  if (!route) return
  callClarity('set', 'route', route)
  callClarity('set', 'page_path', route)
}

export function setClarityMarketingContext(search: string): void {
  if (!search) return
  const params = new URLSearchParams(search)
  const trackingTags: Record<string, string> = {}
  for (const key of TRACKING_QUERY_KEYS) {
    const value = params.get(key)
    if (!value) continue
    trackingTags[key] = value
  }
  setClarityTags(trackingTags)
}

export function trackClarityEvent(eventName: string): void {
  const event = normalizeClarityValue(eventName)
  if (!event) return
  callClarity('event', event)
}

export function setClarityUserContext(params: {
  authenticated: boolean
  role?: string | null
  userId?: string | null
}): void {
  setClarityTags({
    authenticated: params.authenticated ? 'true' : 'false',
    role: params.role,
  })
  if (params.userId) {
    callClarity('identify', params.userId)
  }
}
